import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';

let isInitialized = false;

const SCROLL_DEPTH_THRESHOLDS = [25, 50, 75, 90, 100];

export const initGoogeAnalytics = () => {
	const measurementId = import.meta.env.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID;

	if (!measurementId || isInitialized) return;

	ReactGA.initialize(measurementId);
	isInitialized = true;
};

export const logPageView = (page: string) => {
	if (!import.meta.env.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID) return;

	if (!isInitialized) {
		initGoogeAnalytics();
	}

	ReactGA.send({
		hitType: 'pageview',
		page,
		title: document.title,
	});
};

export const logAnalyticsEvent = (
	eventName: string,
	params?: Record<string, string | number | boolean | undefined>,
) => {
	const measurementId = import.meta.env.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID;

	if (!measurementId) return;

	if (!isInitialized) {
		initGoogeAnalytics();
	}

	ReactGA.event(eventName, {
		debug_mode: import.meta.env.DEV || undefined,
		transport_type: 'beacon',
		...params,
	});
};

const usePageScrollAnalytics = (pagePath: string) => {
	const trackedDepthsRef = useRef<Set<number>>(new Set());
	const animationFrameRef = useRef<number | null>(null);

	useEffect(() => {
		trackedDepthsRef.current = new Set();

		const getPageHeight = () => {
			const body = document.body;
			const html = document.documentElement;

			return Math.max(
				body.scrollHeight,
				body.offsetHeight,
				html.clientHeight,
				html.scrollHeight,
				html.offsetHeight,
			);
		};

		const trackScrollDepth = () => {
			animationFrameRef.current = null;

			const pageHeight = getPageHeight();
			const maxScrollableDistance = pageHeight - window.innerHeight;

			if (maxScrollableDistance <= 0) return;

			const scrollTop = window.scrollY || document.documentElement.scrollTop;
			const viewportBottom = scrollTop + window.innerHeight;
			const percentScrolled =
				viewportBottom >= pageHeight - 2
					? 100
					: Math.floor((scrollTop / maxScrollableDistance) * 100);

			SCROLL_DEPTH_THRESHOLDS.forEach((depth) => {
				if (percentScrolled < depth || trackedDepthsRef.current.has(depth)) return;

				trackedDepthsRef.current.add(depth);
				logAnalyticsEvent('page_scroll_depth', {
					event_category: 'Page Scroll',
					event_label: `${depth}%`,
					page_path: pagePath,
					page_title: document.title,
					percent_scrolled: depth,
					scroll_depth: `${depth}%`,
				});
			});
		};

		const scheduleTracking = () => {
			if (animationFrameRef.current !== null) return;

			animationFrameRef.current = window.requestAnimationFrame(trackScrollDepth);
		};

		scheduleTracking();
		window.addEventListener('scroll', scheduleTracking, { passive: true });
		window.addEventListener('resize', scheduleTracking);

		return () => {
			if (animationFrameRef.current !== null) {
				window.cancelAnimationFrame(animationFrameRef.current);
			}

			window.removeEventListener('scroll', scheduleTracking);
			window.removeEventListener('resize', scheduleTracking);
		};
	}, [pagePath]);
};

const GoogeAnalytics = () => {
	const location = useLocation();
	const pagePath = location.pathname + location.search + location.hash;

	usePageScrollAnalytics(pagePath);

	useEffect(() => {
		logPageView(pagePath);
	}, [pagePath]);

	return null;
};

export default GoogeAnalytics;
